type Environment = "local" | "preview" | "production";

export interface EnvironmentConfig {
  s3Bucket: string;
  s3Key: string;
  apiUrl: string;
  environment: Environment;
}

export const getEnvironment = (): Environment => {
  // Check for Vercel environment variables (available on both client and server)
  if (typeof window !== "undefined") {
    // Client-side: Use NEXT_PUBLIC_ prefixed variables
    if (process.env.NEXT_PUBLIC_VERCEL_ENV === "production")
      return "production";
    if (process.env.NEXT_PUBLIC_VERCEL_ENV === "preview") return "preview";
  } else {
    // Server-side: Use regular Vercel environment variables
    if (process.env.VERCEL_ENV === "production") return "production";
    if (process.env.VERCEL_ENV === "preview") return "preview";
  }

  // Local development
  if (process.env.NODE_ENV === "development") return "local";

  // Default fallback
  return "local";
};

export const getBranchName = (): string => {
  // For Vercel preview deployments
  if (typeof window !== "undefined") {
    // Client-side: Use NEXT_PUBLIC_ prefixed variables
    if (process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF) {
      return process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF;
    }
  } else {
    // Server-side: Use regular Vercel environment variables
    if (process.env.VERCEL_GIT_COMMIT_REF) {
      return process.env.VERCEL_GIT_COMMIT_REF;
    }
  }

  // For local development, you can set this manually or use git
  return process.env.BRANCH_NAME || "local";
};

export const getEnvironmentConfig = (): EnvironmentConfig => {
  const env = getEnvironment();
  const branch = getBranchName();

  if (!process.env.API_URL_PRODUCTION || !process.env.API_URL_DEV) {
    throw new Error(
      `Missing in environment variables: ${!process.env.API_URL_PRODUCTION ? "API_URL_PRODUCTION" : ""} ${!process.env.API_URL_DEV ? "API_URL_DEV" : ""}`,
    );
  }

  const configs: Record<Environment, EnvironmentConfig> = {
    production: {
      s3Bucket: "crowdsec-onboarding-prod",
      s3Key: "journey-data.json",
      apiUrl: process.env.API_URL_PRODUCTION,
      environment: "production",
    },
    preview: {
      s3Bucket: "crowdsec-onboarding-staging",
      s3Key: `journey-data-${branch}.json`,
      apiUrl: process.env.API_URL_DEV,
      environment: "preview",
    },
    local: {
      s3Bucket: "crowdsec-onboarding-dev",
      s3Key: "journey-data-local.json",
      apiUrl: process.env.API_URL_DEV,
      environment: "local",
    },
  };

  return configs[env];
};

export const getAllEnvironmentConfigs = () => {
  const branch = getBranchName();

  // Check for required environment variables
  if (!process.env.API_URL_PRODUCTION || !process.env.API_URL_DEV) {
    throw new Error(
      `Missing in environment variables (all env): ${!process.env.API_URL_PRODUCTION ? "API_URL_PRODUCTION" : ""} ${!process.env.API_URL_DEV ? "API_URL_DEV" : ""}`,
    );
  }

  return {
    production: {
      s3Bucket: "crowdsec-onboarding-prod",
      s3Key: "journey-data.json",
      apiUrl: process.env.API_URL_PRODUCTION,
      environment: "production" as Environment,
    },
    preview: {
      s3Bucket: "crowdsec-onboarding-staging",
      s3Key: `journey-data-${branch}.json`,
      apiUrl: process.env.API_URL_DEV,
      environment: "preview" as Environment,
    },
    local: {
      s3Bucket: "crowdsec-onboarding-dev",
      s3Key: "journey-data-local.json",
      apiUrl: process.env.API_URL_DEV,
      environment: "local" as Environment,
    },
  };
};
